/**
 * Dashboard V2 - Redesigned
 *
 * Clean, tool-focused dashboard inspired by Linear/Vercel/Trade Republic.
 * Thin Slot-Shell: header + a unified 2/3-1/3 page grid. Left column
 * (≈2/3): StatsRow → FavoriteTools → RecentImages. Right column (≈1/3):
 * PlanCard → Sidebar (QuickActions/Transactions/InviteFriends), gated by
 * can_manage_credits.
 */

import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getDashboard } from '@/lib/api/dashboard';
import { getDashboardMetrics } from '@/lib/api/dashboard-metrics';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import { Alert, AlertTitle, AlertDescription, Loading } from '@/components/v2/ui';
import { EID } from '@/lib/element-ids.registry';
import { DashboardHeader } from './DashboardHeader';
import { DashboardStatsRow } from './DashboardStatsRow';
import { DashboardRecentImages } from './DashboardRecentImages';
import { DashboardFavoriteTools } from './DashboardFavoriteTools';
import { DashboardSidebar } from './DashboardSidebar';
import { DashboardPlanCard } from './DashboardPlanCard';

export function DashboardV2() {
  const { t } = useTranslation();
  const { user } = useSupabaseAuth();

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard'],
    queryFn: getDashboard,
  });

  const { data: metrics } = useQuery({
    queryKey: ['dashboard-metrics'],
    queryFn: getDashboardMetrics,
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
        <div className="container mx-auto px-4 py-8">
          <Loading size="lg" label={t('common.loading')} centered />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
        <div className="container mx-auto px-4 py-8">
          <Alert variant="error">
            <AlertTitle>{t('errors.loading')}</AlertTitle>
            <AlertDescription>{t('dashboard.loadError')}</AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const { stats, recent_images, transactions, email_verified, can_manage_credits } = data;

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-950">
      <div className="max-w-[1360px] mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-10">
        <DashboardHeader
          username={user?.username ?? ''}
          imagesThisWeek={metrics?.images_this_week ?? null}
        />

        {!email_verified && (
          <Alert id={EID['dashboard-email-warning-region']} variant="warning" className="mb-6">
            <AlertTitle>{t('dashboard.emailNotVerified')}</AlertTitle>
            <AlertDescription>{t('dashboard.emailVerificationRequired')}</AlertDescription>
          </Alert>
        )}

        <div className={`grid grid-cols-1 gap-6 lg:items-start ${can_manage_credits ? 'lg:grid-cols-3' : ''}`}>
          <div className={`space-y-8 ${can_manage_credits ? 'lg:col-span-2' : ''}`}>
            <DashboardStatsRow
              stats={stats}
              metrics={metrics}
              canManageCredits={can_manage_credits}
            />
            <DashboardFavoriteTools />
            <DashboardRecentImages images={recent_images} />
          </div>

          {can_manage_credits && (
            <div className="space-y-6">
              <DashboardPlanCard
                planName={stats.plan_display_name}
                discountPercentage={stats.discount_percentage}
                productCount={stats.product_count}
                productLimit={stats.product_limit ?? undefined}
              />
              <DashboardSidebar
                transactions={transactions}
                canManageCredits={can_manage_credits}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardV2;
